import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CreatableSelect from 'react-select/creatable';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import style from '../../../../scss/components/form.module.scss';
import '../../../../scss/external/select.scss';
import Field from './Field';

const TagTypeInput = ({ entity, onChange, service, name, ...props }) => {
    const { t } = useTranslation();

    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const currentValue = useMemo(() => {
        const value = entity?.[name];

        if (!value) {
            return null;
        }

        return {
            value,
            label: value,
        };
    }, [entity, name]);

    const [selected, setSelected] = useState(currentValue);

    useEffect(() => {
        setSelected(currentValue);
    }, [currentValue]);

    useEffect(() => {
        let cancelled = false;

        setIsLoading(true);

        service
            .get('tags/types')
            .then((result) => {
                if (cancelled) return;

                const data = Array.isArray(result)
                    ? result
                    : result?.data ?? [];

                const mapped = data.filter(Boolean).map((type) => ({
                    value: type,
                    label: type,
                }));

                setOptions(mapped);
            })
            .catch(() => {
                if (!cancelled) {
                    setOptions([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [service]);

    const handleChange = (value) => {
        setSelected(value);

        onChange({
            [name]: value?.value ?? null,
        });
    };

    return (
        <div className={classNames(style.formGroup, props.className)}>
            <Field {...props} name={name}>
                {(hasError, attributes, isChanged) => (
                    <CreatableSelect
                        inputId={attributes.id}
                        name={name}
                        isClearable
                        isLoading={isLoading}
                        options={options}
                        value={selected}
                        onChange={handleChange}
                        placeholder={t('tags.placeholder')}
                        formatCreateLabel={(inputValue) =>
                            `${t('tags.create')}: ${inputValue}`
                        }
                        className={classNames(
                            style.autocomplete,
                            `-${name}`,
                            { [style.changedInput]: isChanged },
                            { [style.isInvalid]: hasError },
                        )}
                        classNamePrefix="rs"
                        menuPlacement="auto"
                    />
                )}
            </Field>
        </div>
    );
};

TagTypeInput.propTypes = {
    entity: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    service: PropTypes.object.isRequired,
    name: PropTypes.string,
    className: PropTypes.string,
};

TagTypeInput.defaultProps = {
    name: 'type',
    className: '',
};

export default TagTypeInput;
